import React, { useState, useRef, useEffect } from 'react';
import { API } from '../../services/api';
import './CreateApiKeyModal.css';

function CreateApiKeyModal({ onClose, onSuccess }) {
    const [step, setStep] = useState(1); // 1: nom, 2: affichage secret
    const [name, setName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [createdKey, setCreatedKey] = useState(null);
    const [copiedApiKey, setCopiedApiKey] = useState(false);
    const [copiedSecret, setCopiedSecret] = useState(false);
    const nameInputRef = useRef(null);

    useEffect(() => {
        if (step === 1 && nameInputRef.current) {
            nameInputRef.current.focus();
        }
    }, [step]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!name.trim()) {
            setError('Veuillez entrer un nom pour la clé');
            return;
        }

        setLoading(true);
        try {
            const response = await API.createApiKey(name.trim());
            if (response && response.data && response.data.api_key) {
                setCreatedKey(response.data.api_key);
                setStep(2);
            }
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = async (text, type) => {
        try {
            await navigator.clipboard.writeText(text);
            if (type === 'apiKey') {
                setCopiedApiKey(true);
                setTimeout(() => setCopiedApiKey(false), 2000);
            } else {
                setCopiedSecret(true);
                setTimeout(() => setCopiedSecret(false), 2000);
            }
        } catch (err) {
            // Fallback pour les navigateurs plus anciens
            const textarea = document.createElement('textarea');
            textarea.value = text;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            if (type === 'apiKey') {
                setCopiedApiKey(true);
                setTimeout(() => setCopiedApiKey(false), 2000);
            } else {
                setCopiedSecret(true);
                setTimeout(() => setCopiedSecret(false), 2000);
            }
        }
    };

    const handleClose = () => {
        if (step === 2) {
            onSuccess();
        } else {
            onClose();
        }
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content create-apikey-modal">
                <div className="modal-header">
                    <h2>{step === 1 ? 'Créer une clé API' : 'Clé API créée'}</h2>
                    <button className="modal-close" onClick={handleClose}>×</button>
                </div>

                {step === 1 ? (
                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label htmlFor="keyName">Nom de la clé</label>
                            <input
                                ref={nameInputRef}
                                id="keyName"
                                type="text"
                                className="form-input"
                                placeholder="Ex: Plugin Outlook Production"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                maxLength={100}
                            />
                            <div className="form-hint">
                                Donnez un nom descriptif pour identifier l'usage de cette clé.
                            </div>
                        </div>

                        {error && <div className="error-message">{error}</div>}

                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn-secondary"
                                onClick={onClose}
                                disabled={loading}
                            >
                                Annuler
                            </button>
                            <button
                                type="submit"
                                className="btn-primary"
                                disabled={loading}
                            >
                                {loading ? 'Création...' : 'Créer la clé'}
                            </button>
                        </div>
                    </form>
                ) : (
                    <div className="secret-display">
                        <div className="warning-banner">
                            <strong>Important :</strong> Copiez votre clé secrète maintenant.
                            Elle ne sera plus jamais affichée après la fermeture de cette fenêtre.
                        </div>

                        <div className="key-field">
                            <label>Clé API (publique)</label>
                            <div className="key-value-container">
                                <code className="key-value">{createdKey.api_key}</code>
                                <button
                                    type="button"
                                    className="btn-copy"
                                    onClick={() => copyToClipboard(createdKey.api_key, 'apiKey')}
                                >
                                    {copiedApiKey ? 'Copié !' : 'Copier'}
                                </button>
                            </div>
                        </div>

                        <div className="key-field">
                            <label>Clé secrète (privée)</label>
                            <div className="key-value-container">
                                <code className="key-value secret">{createdKey.api_secret}</code>
                                <button
                                    type="button"
                                    className="btn-copy"
                                    onClick={() => copyToClipboard(createdKey.api_secret, 'secret')}
                                >
                                    {copiedSecret ? 'Copié !' : 'Copier'}
                                </button>
                            </div>
                            <div className="form-hint warning">
                                Ne partagez jamais cette clé secrète. Traitez-la comme un mot de passe.
                            </div>
                        </div>

                        <div className="usage-example">
                            <h4>Utilisation dans le plugin Outlook</h4>
                            <p>Lors de la configuration du plugin, entrez ces deux clés dans les champs correspondants.</p>
                        </div>

                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn-primary"
                                onClick={handleClose}
                            >
                                J'ai copié mes clés, fermer
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default CreateApiKeyModal;
